// Who this contact owns or manages, and recording it (§4.7, migration 024).
//
// WHY THIS IS NOT A FIELD ON THE EDIT FORM. `customers.position` already exists and is
// free text an agent types while somebody is on the phone: "GM", "manager", "asst
// mgr", "Manager (Downtown)". It is the right thing for reading one record and useless
// for the question this feature is for — "everyone who manages a store under Marco's",
// asked because a sunset notice or a release note has to go out. A mailing list built
// on LIKE '%manager%' quietly includes the assistant manager and misses the one
// spelled GM, and nobody discovers either until after the send.
//
// AGENT LEVEL, like the rest of the directory writes. An agent who has just been told
// on a call that this person is now the store manager should be able to record it then,
// not file a request that lands in somebody's queue and expires.
//
// THE FORM OPENS POINTED AT THE CONTACT'S OWN STORE. Almost every role recorded here
// is over the account already on screen, so `defaultScope` prefills it and the common
// case is: pick the role, save. The pickers exist for the multi-store owner, who is
// real but rare, and they stay out of the way until the scope is changed.
//
// REMOVAL IS NOT CONFIRMED, deliberately. A role is one row with no history hanging
// off it and re-adding it is two clicks, so a dialog here would be friction protecting
// nothing. This is the opposite call from the account move on EditPanel, which is
// confirmed because it silently reroutes the contact's future tickets.

import { useCallback, useEffect, useState } from 'react';

import { api } from '../../api/client.js';
import { useCanWrite } from '../../session/SessionProvider.jsx';
import OrganizationPicker from './OrganizationPicker.jsx';
import {
  ROLE_ORDER, SCOPE_LABELS, roleLabel, defaultScope, newRoleBody, alreadyHeld, describeRoles,
} from './customer-roles.js';

const SCOPE_ORDER = ['location', 'organization', 'brand'];

const blankDraft = (customer) => {
  const preset = defaultScope(customer);
  return {
    role: 'store_manager',
    scope: preset?.scope ?? 'location',
    scopeId: preset?.scopeId ?? '',
    // The organization the store picker is currently listing. Seeded from the
    // contact's own so the default store is selectable without touching anything.
    organizationId: customer?.organization?.id ? String(customer.organization.id) : '',
    organizationName: customer?.organization?.name ?? '',
  };
};

export default function RolesPanel({ customer }) {
  const canWrite = useCanWrite();
  const customerId = customer?.id;

  const [roles, setRoles] = useState({ status: 'loading', rows: [], error: null });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(() => blankDraft(customer));
  const [brands, setBrands] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setDraft((previous) => ({ ...previous, [field]: value }));

  const load = useCallback((signal) => api
    .get(`/customers/${encodeURIComponent(customerId)}/roles`, { signal })
    .then((body) => setRoles({ status: 'ready', rows: body?.data ?? [], error: null }))
    .catch((failure) => {
      if (failure?.name === 'AbortError') return;
      setRoles({ status: 'error', rows: [], error: failure });
    }), [customerId]);

  useEffect(() => {
    if (!customerId) return undefined;
    const controller = new AbortController();
    setRoles((previous) => ({ ...previous, status: 'loading', error: null }));
    load(controller.signal);
    return () => controller.abort();
  }, [customerId, load]);

  // Reset the form when the panel moves to a different contact, or its default store
  // stays pointed at the previous one.
  useEffect(() => {
    setDraft(blankDraft(customer));
    setAdding(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Three rows, fetched once and only when a brand can actually be chosen.
  useEffect(() => {
    if (!adding || draft.scope !== 'brand' || brands.length > 0) return undefined;
    const controller = new AbortController();
    api.get('/brands', { signal: controller.signal })
      .then((body) => setBrands(body?.data ?? []))
      .catch(() => setBrands([]));
    return () => controller.abort();
  }, [adding, draft.scope, brands.length]);

  // The stores of whichever organization the picker is on. Same rule EditPanel uses.
  useEffect(() => {
    if (!draft.organizationId) { setLocations([]); return undefined; }
    const controller = new AbortController();
    api.get(`/organizations/${draft.organizationId}/locations`, { signal: controller.signal })
      .then((body) => setLocations(body?.data ?? []))
      .catch(() => setLocations([]));
    return () => controller.abort();
  }, [draft.organizationId]);

  async function add() {
    const body = newRoleBody(draft);
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      // The POST answers with the CURRENT full list, so there is nothing to merge and
      // no window where the panel disagrees with the server about what was just saved.
      const response = await api.post(`/customers/${customer.id}/roles`, body);
      setRoles({ status: 'ready', rows: response?.data ?? [], error: null });
      setAdding(false);
      setDraft(blankDraft(customer));
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function remove(roleId) {
    setBusy(true);
    setError(null);
    try {
      const response = await api.del(`/customers/${customer.id}/roles/${roleId}`);
      setRoles({ status: 'ready', rows: response?.data ?? [], error: null });
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  const rows = describeRoles(roles.rows);
  const held = alreadyHeld(roles.rows, draft);
  const complete = Boolean(newRoleBody(draft));

  // Which control names the thing the role is over. Store needs two: an organization
  // to list, then one of its stores.
  const target = () => {
    if (draft.scope === 'brand') {
      return (
        <select
          className="field-input"
          value={draft.scopeId}
          onChange={(event) => set('scopeId', event.target.value)}
        >
          <option value="">Choose a brand…</option>
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.name}
              {brand.is_active ? '' : ' (retired)'}
            </option>
          ))}
        </select>
      );
    }

    const picker = (
      <OrganizationPicker
        organizationId={draft.scope === 'organization' ? draft.scopeId : draft.organizationId}
        organizationName={draft.organizationName}
        onChange={(next) => setDraft((previous) => ({
          ...previous,
          organizationId: next.organizationId,
          organizationName: next.organizationName,
          // Choosing a different organization invalidates whichever store was picked
          // from the previous one; the server refuses the mismatch anyway.
          scopeId: previous.scope === 'organization' ? next.organizationId : '',
        }))}
      />
    );

    if (draft.scope === 'organization') return picker;

    return (
      <div className="space-y-2">
        {picker}
        <select
          className="field-input"
          value={draft.scopeId}
          disabled={!draft.organizationId}
          onChange={(event) => set('scopeId', event.target.value)}
        >
          <option value="">
            {draft.organizationId ? 'Choose a store…' : 'Choose an organization first'}
          </option>
          {locations.map((location) => (
            <option key={location.id} value={location.id}>{location.name}</option>
          ))}
        </select>
      </div>
    );
  };

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Roles</h2>
        {canWrite && !adding && (
          <button
            type="button"
            className="btn-ghost ring-1 ring-line-strong"
            onClick={() => { setDraft(blankDraft(customer)); setAdding(true); setError(null); }}
          >
            Add role
          </button>
        )}
      </div>

      {roles.status === 'error' && (
        <p className="mt-2 text-sm text-red-700 dark:text-red-300">
          Could not load this contact&rsquo;s roles. {roles.error?.message}
        </p>
      )}

      {roles.status !== 'error' && rows.length === 0 && !adding && (
        <p className="mt-2 text-sm text-ink-muted">
          {roles.status === 'loading'
            ? 'Loading…'
            : 'No roles recorded. Roles are what put somebody on a targeted send, like a sunset notice or a release note.'}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3">
              <span className="min-w-0 text-ink">
                <span className="font-medium">{row.label}</span>
                <span className="text-ink-secondary">
                  {' '}&middot; {row.scopeLabel} {row.scopeName}
                </span>
              </span>
              {canWrite && (
                <button
                  type="button"
                  className="shrink-0 text-xs text-ink-muted hover:text-red-700 disabled:opacity-50 dark:hover:text-red-300"
                  disabled={busy}
                  onClick={() => remove(row.id)}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-ink-secondary">Role</span>
              <select
                className="field-input mt-1"
                value={draft.role}
                onChange={(event) => set('role', event.target.value)}
              >
                {ROLE_ORDER.map((role) => (
                  <option key={role} value={role}>{roleLabel(role)}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-ink-secondary">Held over</span>
              <select
                className="field-input mt-1"
                value={draft.scope}
                onChange={(event) => setDraft((previous) => ({
                  ...previous,
                  scope: event.target.value,
                  // The id belongs to the old scope's table and would name an
                  // unrelated row under the new one.
                  scopeId: event.target.value === 'organization' ? previous.organizationId : '',
                }))}
              >
                {SCOPE_ORDER.map((scope) => (
                  <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <span className="text-xs font-medium text-ink-secondary">
              {SCOPE_LABELS[draft.scope]}
            </span>
            <div className="mt-1">{target()}</div>
          </div>

          {error && (
            <p className="text-sm text-red-700 dark:text-red-300">{error.message}</p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !complete || held}
              onClick={add}
            >
              {busy ? 'Saving…' : 'Save role'}
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={busy}
              onClick={() => { setAdding(false); setError(null); }}
            >
              Cancel
            </button>
            {held && (
              <span className="text-xs text-ink-muted">
                Already recorded.
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
